import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";

import { useFormatCurrency } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import Radio from "@krea/customer-features/shared-components/fields/radio";
import RadioGroup from "@krea/customer-features/shared-components/fields/radiogroup";
import { Text } from "@krea/customer-features/shared-components/text";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const SuggestedAmountForm = ({
  fieldRequestID,
  isPreviewMode,
  t,
  withInputLabel,
}) => {
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);

  const errors = error?.data?.errors || [];
  const currency = settings.currency;
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const [state, setState] = useState({
    useOriginalAmountOnInit: submittedData?.use_original_amount || null,
    useOriginalAmount: submittedData?.use_original_amount || false,
    originalRequestedAmount:
      submittedData?.original_requested_loan_amount || null,
    newAmount: submittedData?.preliminary_bid.estimated_max_loan_amount || null,
    touched: false,
  });

  const radioOptions = [
    {
      label: t("iaq.suggestedAmount.options.dontUseOriginalAmount", {
        estimatedMaxLoanAmount: formattedCurrencyValue(state.newAmount),
      }),
      value: false,
    },
    {
      label: `${t("iaq.suggestedAmount.options.useOriginalAmount")} (${formattedCurrencyValue(
        state.originalRequestedAmount,
      )})`,
      value: true,
    },
  ];

  useEffect(() => {
    if (state.useOriginalAmount || state.useOriginalAmountOnInit === null) {
      setState((state) => ({
        ...state,
        touched: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleProperty = ({ target }) => {
    const { value } = target;
    const isTrue = value === "true";

    setState((state) => ({
      ...state,
      useOriginalAmountOnInit: isTrue,
      useOriginalAmount: isTrue,
      touched: state.useOriginalAmount !== value,
    }));
  };

  const setFocus = ({ target }) => {
    target.focus();
  };

  const onBlur = () => {
    const payload = {
      use_original_amount: state.useOriginalAmount,
      new_amount: state.newAmount,
    };

    if (state.touched) {
      postFieldRequest(payload);

      setState((state) => ({
        ...state,
        touched: false,
      }));
    }
  };

  const getTitleText = (data) => {
    if (data) {
      return state.useOriginalAmount
        ? radioOptions[0].label
        : radioOptions[1].label;
    }
  };

  useEffect(() => {
    state.touched && onBlur();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          label={displayName}
          value={state.useOriginalAmount && <>{getTitleText(submittedData)}</>}
        />
      ) : (
        <>
          <form className="input-form" onSubmit={(e) => e.preventDefault()}>
            <Text size="lg" className="font-weight-bold mb-5">
              {t("iaq.suggestedAmount.description", {
                estimatedMaxLoanAmount: formattedCurrencyValue(state.newAmount),
              })}
            </Text>

            <RadioGroup
              name="group3"
              label={withInputLabel && displayName}
              onChange={toggleProperty}
            >
              {radioOptions.map((radioValue) => (
                <Radio
                  key={radioValue.value}
                  id={radioValue.label}
                  value={radioValue.value}
                  label={radioValue.label}
                  onClick={setFocus}
                  checked={state.useOriginalAmount === radioValue.value}
                />
              ))}
            </RadioGroup>
          </form>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}

      {errors ? (
        <>
          {errors.map((error, index) => (
            <Error className="mt-3" key={index}>
              {error.message}
            </Error>
          ))}
        </>
      ) : null}
    </>
  );
};

export default withTranslation()(SuggestedAmountForm);
